import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { Constants } from '@shared/models';
import type { TMessage } from '@shared/models';
import { ViewBasePage } from '@shared/pages';
import { MessageCenterService } from '@shared/services';

@Component({
    'selector': 'app-messageCenter',
    'templateUrl': './messageCenter.page.html',
    'styleUrls': ['./messageCenter.page.scss'],
    'standalone': true,
    'imports': [CommonModule, FormsModule, IonicModule]
})
export class MessageCenterPage extends ViewBasePage implements OnInit, OnDestroy {
    public messages: TMessage[] = [];
    public selectedMessage: TMessage | null = null;
    public unreadCount = 0;
    public messagesDefinedStr = '';
    private messagesSubscription: any;
    public debugMode = true; // Set to true to show debug-only features

    constructor(
        private messageCenterService: MessageCenterService,
        injector: Injector
    ) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(Constants.VIEW_MESSAGE_CENTER);
    }

    public override ngOnInit(): void {
        super.ngOnInit();
        
        this.debugMode = this.sessionService.isDebugMode();
        if (this.debugMode) {
            this.loadAllMessages();
        } else {
            // Use filtered observable to only show messages targeted to the current user
            this.loadUserMessages();
        }
        // let messagesObservable;
        // if (this.debugMode) {
        //     messagesObservable = this.messageCenterService.getObservable();
        // } else {
        //     messagesObservable = this.messageCenterService.getFilteredObservable();
        // }

        // this.messagesSubscription = messagesObservable.subscribe((messages: TMessage[]) => {
        //     this.messages = this.messageCenterService.sort(
        //         messages.filter(m => m.status !== 'dismissed')
        //     );
        //     this.unreadCount = this.messages.filter(m => m.status === 'unread').length;
        //     this.messagesDefinedStr = this.utilsService
        //         .useSingularOrPlural(this.messages.length, 'message.', 'messages.');

        //     // On wide screens, auto-select the first message if none selected
        //     if (!this.selectedMessage && this.messages.length > 0 && this.isWideScreen()) {
        //         this.selectMessage(this.messages[0]);
        //     }
        // });

        this.initMoreMenu();
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.messagesSubscription) {
            this.messagesSubscription.unsubscribe();
        }
    }

    private initMoreMenu(): void {
        const menuitem1 = { name: 'Compute Next Rule' };
        const menuitem2 = { name: 'Load All Messages' };
        const menuitem3 = { name: 'Filter Messages By Logged In User' };

        this.resetPopoverMenu();
        this.pushPopoverMenuItem(menuitem1.name);
        this.pushPopoverMenuItem(menuitem2.name);
        this.pushPopoverMenuItem(menuitem3.name);

        this.moreMenuItemClick((menuItem: any) => {
            switch (menuItem.name) {
                case menuitem1.name:
                    this.computeNextRule();
                    break;

                case menuitem2.name:
                    this.loadAllMessages();
                    break;

                case menuitem3.name:
                    this.loadUserMessages();
                    break;
            }
        });
    }

    private async computeNextRule(): Promise<void> {
        const accountNumber = this.sessionService.getAccountNumber();
        await this.firebaseFunctionsService.evaluateRewardFunnelRules(accountNumber);
    }

    private loadAllMessages(): void {
        if (this.messagesSubscription) {
            this.messagesSubscription.unsubscribe();
        }
        this.messagesSubscription = this.messageCenterService.getObservable().subscribe((messages: TMessage[]) => {
            this.messages = this.messageCenterService.sort(
                messages.filter(m => m.status !== 'dismissed')
            );
            this.unreadCount = this.messages.filter(m => m.status === 'unread').length;
            this.messagesDefinedStr = this.utilsService
                .useSingularOrPlural(this.messages.length, 'message.', 'messages.');

            // On wide screens, auto-select the first message if none selected
            if (!this.selectedMessage && this.messages.length > 0 && this.isWideScreen()) {
                this.selectMessage(this.messages[0]);
            }
        });
    }

    private loadUserMessages(): void {
        if (this.messagesSubscription) {
            this.messagesSubscription.unsubscribe();
        }
        this.messagesSubscription = this.messageCenterService.getFilteredObservable().subscribe((messages: TMessage[]) => {
            this.messages = this.messageCenterService.sort(
                messages.filter(m => m.status !== 'dismissed')
            );
            this.unreadCount = this.messages.filter(m => m.status === 'unread').length;
            this.messagesDefinedStr = this.utilsService
                .useSingularOrPlural(this.messages.length, 'message.', 'messages.');

            // On wide screens, auto-select the first message if none selected
            if (!this.selectedMessage && this.messages.length > 0 && this.isWideScreen()) {
                this.selectMessage(this.messages[0]);
            }
        });
    }

    /**
     * Handle message click. On narrow screens, navigate to detail page.
     * On wide screens, show in the side panel.
     */
    public onMessageClick(message: TMessage): void {
        if (this.isWideScreen()) {
            this.selectMessage(message);
        } else {
            this.messageCenterService.markAsRead(message.uid);
            this.router.navigateByUrl(`/help/messageCenter/message/${message.uid}`);
        }
    }

    /**
     * Select a message for the detail panel (wide screen mode).
     */
    public selectMessage(message: TMessage): void {
        this.selectedMessage = message;
        this.messageCenterService.markAsRead(message.uid);
    }

    /**
     * Dismiss a message.
     */
    public async onDismissMessage(message: TMessage): Promise<void> {
        await this.messageCenterService.dismiss(message.uid);
        if (this.selectedMessage?.uid === message.uid) {
            this.selectedMessage = this.messages.length > 0 ? this.messages[0] : null;
        }
    }

    /**
     * Navigate to the action URL of a message, if defined.
     */
    public onActionClick(message: TMessage): void {
        if (message.actionUrl) {
            this.router.navigateByUrl(message.actionUrl);
        }
    }

    /**
     * Returns the Ionic color for a given message type.
     */
    public getTypeColor(type: string): string {
        switch (type) {
            case 'warning': return 'warning';
            case 'action': return 'danger';
            case 'announcement': return 'tertiary';
            case 'info':
            default: return 'primary';
        }
    }

    /**
     * Returns the icon name for a given message type (fallback if no custom icon).
     */
    public getTypeIcon(message: TMessage): string {
        if (message.icon) return message.icon;
        switch (message.type) {
            case 'warning': return 'alert-circle';
            case 'action': return 'flash';
            case 'announcement': return 'megaphone';
            case 'info':
            default: return 'information-circle';
        }
    }

    /**
     * Returns a formatted date string.
     */
    public formatDate(date: any): string {
        if (date instanceof Date) {
            return date.toLocaleDateString();
        }
        return '';
    }

    /**
     * Checks if the current viewport is wide enough for the split layout.
     */
    public isWideScreen(): boolean {
        return window.innerWidth >= 768;
    }

    /**
     * Returns true if the message is the currently selected one.
     */
    public isSelected(message: TMessage): boolean {
        return this.selectedMessage?.uid === message.uid;
    }
}
